import { ToastInput, ToastPromiseMessages, ToastTone } from '../../../types/DesignSystem/Toast';
import { dismissAllToasts, dismissToast, pushToast } from './toastStore';

/**
 * The public API for raising a toast.
 *
 * Deliberately **imperative**, and deliberately **not a hook-only API**. A toast is
 * raised at the moment something finishes, and in this app that is frequently outside
 * the component tree — an axios interceptor, the `CanvasAutoSave` bridge, a promote
 * handler. So `toast` is a plain object usable from any module:
 *
 * ```ts
 * import { toast } from '.../Toast/useToast';
 *
 * toast.success('Model promoted');
 * toast.error('Could not save', { description: 'The submodel is locked by another user.' });
 * const id = toast.info('Saving…', { duration: null });
 * toast.success('Saved', { id });     // replaces it in place
 * ```
 *
 * A declarative `<Toast open={…}>` was rejected: every caller would have to hold the
 * open flag, the copy and a timer in its own state, which is how apps end up with six
 * different notification implementations. The one thing lost is that a toast is not
 * described by the render tree — which is why `ToastRegion` reads a store rather than
 * props, and why `resetToasts()` exists for tests.
 */

type Options = Omit<ToastInput, 'title' | 'tone'>;

const raise = (tone: ToastTone) => (title: string, options: Options = {}): string =>
  pushToast({ ...options, title, tone });

const resolveMessage = <V,>(message: string | ((v: V) => string), value: V): string =>
  typeof message === 'function' ? message(value) : message;

/**
 * § 303 — the "Saving… → Saved" idiom as ONE call. The docblock above shows the
 * two-call version (`toast.info` with `duration: null`, then `toast.success` with
 * the same `id`); every caller of it held an id and remembered to replace. This does
 * that: a sticky `info` toast while the promise is pending, replaced in place by
 * `success` (the tone's default duration) or a sticky `danger` on rejection — the
 * announcer hears the second message because a replace bumps `seq`. Returns the
 * ORIGINAL promise, so the caller still awaits and catches it: this observes, it
 * never swallows.
 */
const promise = <T,>(
  work: Promise<T>,
  messages: ToastPromiseMessages<T>,
  options: Omit<Options, 'duration' | 'id'> = {},
): Promise<T> => {
  const id = pushToast({ ...options, title: messages.loading, tone: 'info', duration: null });
  work.then(
    (value) => { pushToast({ ...options, id, title: resolveMessage(messages.success, value), tone: 'success' }); },
    (error: unknown) => { pushToast({ ...options, id, title: resolveMessage(messages.error, error), tone: 'danger' }); },
  );
  return work;
};

export const toast = {
  /** Neutral notification — no status colour. */
  show: (title: string, options: Options = {}) => pushToast({ ...options, title }),
  info: raise('info'),
  success: raise('success'),
  warning: raise('warning'),
  /** Sticky by default — see `DEFAULT_DURATION` and SC 2.2.1. */
  error: raise('danger'),
  /** § 303 — loading → success | error, one toast replaced in place. */
  promise,
  dismiss: dismissToast,
  dismissAll: dismissAllToasts,
};

/**
 * Hook form, for callers that prefer one. It returns the same singleton, so there is
 * no provider to mount and no second store — `ToastRegion` is the only thing that
 * has to exist in the tree.
 */
export const useToast = () => toast;
